<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, nextTick } from 'vue'

const isDebugEnabled = computed(() => import.meta.env.VITE_DEBUG === 'true')

interface LogEntry {
  id: number
  level: 'log' | 'info' | 'warn' | 'error'
  message: string
  timestamp: Date
}

interface StorageEntry {
  key: string
  value: string
  size: number
}

const isOpen = ref(false)
const logs = ref<LogEntry[]>([])
const logContainer = ref<HTMLElement | null>(null)
const activeTab = ref<'console' | 'storage'>('console')
const storageItems = ref<StorageEntry[]>([])
const expandedKeys = ref<Set<string>>(new Set())
let logId = 0

// Store original console methods
const originalConsole = {
  log: console.log.bind(console),
  info: console.info.bind(console),
  warn: console.warn.bind(console),
  error: console.error.bind(console),
}

function formatArgs(args: any[]): string {
  return args.map(arg => {
    if (arg === null) return 'null'
    if (arg === undefined) return 'undefined'
    if (typeof arg === 'object') {
      try {
        return JSON.stringify(arg, null, 2)
      } catch {
        return String(arg)
      }
    }
    return String(arg)
  }).join(' ')
}

function addLog(level: LogEntry['level'], ...args: any[]) {
  logs.value.push({
    id: logId++,
    level,
    message: formatArgs(args),
    timestamp: new Date(),
  })

  // Keep only last 200 logs
  if (logs.value.length > 200) {
    logs.value = logs.value.slice(-200)
  }

  // Auto-scroll to bottom
  nextTick(() => {
    if (logContainer.value) {
      logContainer.value.scrollTop = logContainer.value.scrollHeight
    }
  })
}

function interceptConsole() {
  console.log = (...args) => {
    originalConsole.log(...args)
    addLog('log', ...args)
  }
  console.info = (...args) => {
    originalConsole.info(...args)
    addLog('info', ...args)
  }
  console.warn = (...args) => {
    originalConsole.warn(...args)
    addLog('warn', ...args)
  }
  console.error = (...args) => {
    originalConsole.error(...args)
    addLog('error', ...args)
  }
}

function restoreConsole() {
  console.log = originalConsole.log
  console.info = originalConsole.info
  console.warn = originalConsole.warn
  console.error = originalConsole.error
}

function clearLogs() {
  logs.value = []
}

// LocalStorage functions
function refreshStorage() {
  const items: StorageEntry[] = []
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i)
    if (key) {
      const value = localStorage.getItem(key) || ''
      items.push({
        key,
        value,
        size: new Blob([value]).size,
      })
    }
  }
  items.sort((a, b) => a.key.localeCompare(b.key))
  storageItems.value = items
}

function clearStorage() {
  if (confirm('Clear all localStorage? This cannot be undone.')) {
    localStorage.clear()
    refreshStorage()
    console.log('[DebugConsole] LocalStorage cleared')
  }
}

function deleteStorageItem(key: string) {
  localStorage.removeItem(key)
  expandedKeys.value.delete(key)
  refreshStorage()
  console.log(`[DebugConsole] Deleted localStorage key: ${key}`)
}

function toggleExpand(key: string) {
  if (expandedKeys.value.has(key)) {
    expandedKeys.value.delete(key)
  } else {
    expandedKeys.value.add(key)
  }
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  return `${(bytes / 1024).toFixed(1)} KB`
}

function formatValue(value: string): string {
  try {
    return JSON.stringify(JSON.parse(value), null, 2)
  } catch {
    return value
  }
}

function formatTime(date: Date): string {
  return date.toLocaleTimeString('en-US', {
    hour12: false,
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }) + '.' + String(date.getMilliseconds()).padStart(3, '0')
}

function getLevelColor(level: LogEntry['level']): string {
  switch (level) {
    case 'error': return 'text-red-400'
    case 'warn': return 'text-yellow-400'
    case 'info': return 'text-blue-400'
    default: return 'text-gray-300'
  }
}

function getLevelBg(level: LogEntry['level']): string {
  switch (level) {
    case 'error': return 'bg-red-900/30'
    case 'warn': return 'bg-yellow-900/30'
    default: return ''
  }
}

onMounted(() => {
  interceptConsole()
  refreshStorage()
  // Add initial log
  console.log('[DebugConsole] Initialized')
})

onUnmounted(() => {
  restoreConsole()
})
</script>

<template>
  <template v-if="isDebugEnabled">
    <!-- Floating toggle button -->
    <button
      v-if="!isOpen"
    @click="isOpen = true"
    class="fixed bottom-4 right-4 z-50 w-12 h-12 bg-gray-800 hover:bg-gray-700 text-white rounded-full shadow-lg flex items-center justify-center transition-colors"
    title="Open Debug Console"
  >
    <svg xmlns="http://www.w3.org/2000/svg" class="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
      <polyline points="4 17 10 11 4 5"></polyline>
      <line x1="12" y1="19" x2="20" y2="19"></line>
    </svg>
    <span
      v-if="logs.some(l => l.level === 'error')"
      class="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full text-xs flex items-center justify-center"
    >
      !
    </span>
  </button>

  <!-- Console panel -->
  <div
    v-if="isOpen"
    class="fixed z-50 bg-gray-900 text-white shadow-2xl border border-gray-700 bottom-0 right-0 w-full md:w-[600px] h-[50vh] md:h-[400px] md:bottom-4 md:right-4 md:rounded-lg"
  >
    <!-- Header -->
    <div class="flex items-center justify-between px-3 py-2 bg-gray-800 border-b border-gray-700 rounded-t-lg">
      <div class="flex items-center gap-2">
        <!-- Tabs -->
        <button
          @click="activeTab = 'console'"
          class="flex items-center gap-1 px-2 py-1 text-sm rounded transition-colors"
          :class="activeTab === 'console' ? 'bg-gray-700 text-white' : 'text-gray-400 hover:text-white'"
        >
          <svg xmlns="http://www.w3.org/2000/svg" class="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <polyline points="4 17 10 11 4 5"></polyline>
            <line x1="12" y1="19" x2="20" y2="19"></line>
          </svg>
          <span>Console</span>
          <span class="text-xs text-gray-500">({{ logs.length }})</span>
        </button>
        <button
          @click="activeTab = 'storage'; refreshStorage()"
          class="flex items-center gap-1 px-2 py-1 text-sm rounded transition-colors"
          :class="activeTab === 'storage' ? 'bg-gray-700 text-white' : 'text-gray-400 hover:text-white'"
        >
          <svg xmlns="http://www.w3.org/2000/svg" class="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <ellipse cx="12" cy="5" rx="9" ry="3"></ellipse>
            <path d="M3 5V19A9 3 0 0 0 21 19V5"></path>
            <path d="M3 12A9 3 0 0 0 21 12"></path>
          </svg>
          <span>Storage</span>
          <span class="text-xs text-gray-500">({{ storageItems.length }})</span>
        </button>
      </div>
      <div class="flex items-center gap-1">
        <!-- Clear button (context-aware) -->
        <button
          v-if="activeTab === 'console'"
          @click="clearLogs"
          class="p-1.5 hover:bg-gray-700 rounded transition-colors"
          title="Clear logs"
        >
          <svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M3 6h18"></path>
            <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"></path>
          </svg>
        </button>
        <button
          v-if="activeTab === 'storage'"
          @click="refreshStorage"
          class="p-1.5 hover:bg-gray-700 rounded transition-colors"
          title="Refresh storage"
        >
          <svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M21 12a9 9 0 0 0-9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"></path>
            <path d="M3 3v5h5"></path>
            <path d="M3 12a9 9 0 0 0 9 9 9.75 9.75 0 0 0 6.74-2.74L21 16"></path>
            <path d="M16 16h5v5"></path>
          </svg>
        </button>
        <button
          v-if="activeTab === 'storage'"
          @click="clearStorage"
          class="p-1.5 hover:bg-gray-700 rounded transition-colors text-red-400 hover:text-red-300"
          title="Clear all storage"
        >
          <svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M3 6h18"></path>
            <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"></path>
          </svg>
        </button>
        <button
          @click="isOpen = false"
          class="p-1.5 hover:bg-gray-700 rounded transition-colors"
          title="Close"
        >
          <svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        </button>
      </div>
    </div>

    <!-- Content area -->
    <div class="h-[calc(100%-40px)] overflow-y-auto font-mono text-xs">
      <!-- Console tab -->
      <div v-if="activeTab === 'console'" ref="logContainer" class="h-full overflow-y-auto">
        <div v-if="logs.length === 0" class="p-4 text-gray-500 text-center">
          No logs yet
        </div>
        <div
          v-for="log in logs"
          :key="log.id"
          class="px-3 py-1.5 border-b border-gray-800 hover:bg-gray-800/50"
          :class="getLevelBg(log.level)"
        >
          <div class="flex items-start gap-2">
            <span class="text-gray-500 shrink-0">{{ formatTime(log.timestamp) }}</span>
            <span
              class="shrink-0 uppercase text-[10px] font-bold px-1 rounded"
              :class="getLevelColor(log.level)"
            >
              {{ log.level }}
            </span>
            <pre class="whitespace-pre-wrap break-all flex-1" :class="getLevelColor(log.level)">{{ log.message }}</pre>
          </div>
        </div>
      </div>

      <!-- Storage tab -->
      <div v-if="activeTab === 'storage'" class="h-full overflow-y-auto">
        <div v-if="storageItems.length === 0" class="p-4 text-gray-500 text-center">
          LocalStorage is empty
        </div>
        <div
          v-for="item in storageItems"
          :key="item.key"
          class="border-b border-gray-800"
        >
          <!-- Key header -->
          <div
            class="flex items-center justify-between px-3 py-2 hover:bg-gray-800/50 cursor-pointer"
            @click="toggleExpand(item.key)"
          >
            <div class="flex items-center gap-2 min-w-0 flex-1">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="w-3 h-3 text-gray-500 shrink-0 transition-transform"
                :class="{ 'rotate-90': expandedKeys.has(item.key) }"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <polyline points="9 18 15 12 9 6"></polyline>
              </svg>
              <span class="text-blue-400 truncate">{{ item.key }}</span>
              <span class="text-gray-600 text-[10px] shrink-0">{{ formatBytes(item.size) }}</span>
            </div>
            <button
              @click.stop="deleteStorageItem(item.key)"
              class="p-1 hover:bg-red-900/50 rounded text-gray-500 hover:text-red-400 transition-colors shrink-0"
              title="Delete"
            >
              <svg xmlns="http://www.w3.org/2000/svg" class="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>
          </div>
          <!-- Expanded value -->
          <div
            v-if="expandedKeys.has(item.key)"
            class="px-3 pb-2 pl-8"
          >
            <pre class="text-gray-300 whitespace-pre-wrap break-all bg-gray-800/50 p-2 rounded max-h-48 overflow-y-auto">{{ formatValue(item.value) }}</pre>
          </div>
        </div>
      </div>
    </div>
  </div>
  </template>
</template>
